import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ErrorService, PROJECT_CONTEXT_TOKEN } from '@cosmos/angular/utils';
import { environment } from 'environments/environment';
import { catchError, map } from 'rxjs';
import { StrengthTestResult } from '@cosmos/shared';

@Injectable({
  providedIn: 'root',
})
export class StrengthTestResultsService {
  constructor(private http: HttpClient, private errorService: ErrorService) {}

  findById(id: string, query = '', withRecommendations = false) {
    const sep = query.includes('?') ? '&' : '?';
    const qs = withRecommendations ? `${query}${sep}recommendations=true` : query;
    return this.http
      .get<StrengthTestResult>(
        `${environment.API_URL}/analytics/strength-test-results/${id}${qs}`,
        { observe: 'response' }
      )
      .pipe(
        map((response) => response),
        catchError((error) => { this.errorService.handleError(error); throw error; })
      );
  }

  createStrengthTestResult(payload: any, projectId?: string) {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post(`${environment.API_URL}/analytics/strength-test-results`, payload, {
        observe: 'response',
        context,
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  findAll(query = '') {
    return this.http
      .get(`${environment.API_URL}/analytics/strength-test-results${query}`, {
        observe: 'response',
      })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  update(payload: any, projectId?: string) {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .patch(`${environment.API_URL}/analytics/strength-test-results/${payload[0]}`, payload[1], {
        observe: 'response',
        context,
      })
      .pipe(
        map((response) => response),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  delete(id: string) {
    return this.http
      .delete(`${environment.API_URL}/analytics/strength-test-results/${id}`, {
        observe: 'response',
      })
      .pipe(
        map((response) => response),
        catchError((error) =>{this.errorService.handleError(error); throw error;})
      );
  }

  /**
   * Part 3: Resolve the concrete field on an STR to a ConcreteMixture ObjectId.
   * Called at PENDING_REVIEW step when user selects or confirms a concrete from the dropdown.
   */
  resolveConcrete(strId: string, concreteId: string, projectId?: string) {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post(
        `${environment.API_URL}/analytics/strength-test-results/${strId}/resolve-concrete`,
        { concreteId },
        { observe: 'response', context }
      )
      .pipe(
        map((response) => response),
        catchError((error) => { this.errorService.handleError(error); throw error; })
      );
  }

  attachFile(strId: string, file: File, projectId?: string) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('filename', file.name);
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post(
        `${environment.API_URL}/analytics/strength-test-results/${strId}/files`,
        formData,
        { observe: 'response', context }
      )
      .pipe(
        map((response) => response),
        catchError((error) => { this.errorService.handleError(error); throw error; })
      );
  }

  serveStrFileBlob(strId: string, fileId: string) {
    return this.http
      .get(
        `${environment.API_URL}/analytics/strength-test-results/${strId}/files/${fileId}/serve`,
        { responseType: 'blob', withCredentials: true }
      )
      .pipe(
        map((blob) => ({ url: URL.createObjectURL(blob), contentType: blob.type })),
        catchError((error) => { this.errorService.handleError(error); throw error; })
      );
  }

  findByPour(pourId: string) {
    return this.http
      .get(`${environment.API_URL}/analytics/strength-test-results?sourcePour=${pourId}&populate_concrete=concreteType,name`, {
        observe: 'response',
      })
      .pipe(
        map((response) => response),
        catchError((error) => { this.errorService.handleError(error); throw error; })
      );
  }

  /**
   * Find STRs whose sourcePour is in the given list of pour IDs.
   * Used by the lot page to fetch excluded STRs (pours linked to lot but STR not in lot.testResults).
   */
  findByPours(pourIds: string[], params: Record<string, string> = {}) {
    const qs = new URLSearchParams({
      sourcePours: pourIds.join(','),
      ...params,
    }).toString();
    return this.http
      .get<any[]>(`${environment.API_URL}/analytics/strength-test-results?${qs}`)
      .pipe(catchError((error) => { this.errorService.handleError(error); throw error; }));
  }

  /**
   * Find STRs by controlLot ID — returns all STRs currently included in the lot.
   */
  findByLot(lotId: string, params: Record<string, string> = {}) {
    const qs = new URLSearchParams({
      controlLot: lotId,
      ...params,
    }).toString();
    return this.http
      .get<any[]>(`${environment.API_URL}/analytics/strength-test-results?${qs}`)
      .pipe(catchError((error) => { this.errorService.handleError(error); throw error; }));
  }

  /**
   * Part 8: Link an STR to a ConcretePour (Pour-first model).
   * Sets STR.sourcePour and writes linkMetadata audit trail.
   */
  linkToPour(
    strId: string,
    pourId: string,
    linkedVia: 'recommendation' | 'manual' = 'manual',
    confidence: 'high' | 'medium' | null = null,
    recommendedPourIds: string[] = [],
    projectId?: string
  ) {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .post(
        `${environment.API_URL}/analytics/strength-test-results/${strId}/link-pour`,
        { pourId, linkedVia, confidence, recommendedPourIds },
        { observe: 'response', context }
      )
      .pipe(
        map((response) => response),
        catchError((error) => { this.errorService.handleError(error); throw error; })
      );
  }

  completeFields(
    strId: string,
    fields: Array<{ path: string; value: any }>,
    projectId?: string
  ) {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .patch(
        `${environment.API_URL}/analytics/strength-test-results/${strId}/complete`,
        { fields },
        { observe: 'response', context }
      )
      .pipe(
        map((response) => response),
        catchError((error) => { this.errorService.handleError(error); throw error; })
      );
  }

  updateSlump(
    strId: string,
    dto: { slump0?: number; slump1?: number; slumpUnits: 'cm' | 'mm' },
    projectId?: string
  ) {
    const context = projectId ? new HttpContext().set(PROJECT_CONTEXT_TOKEN, projectId) : undefined;
    return this.http
      .patch(
        `${environment.API_URL}/analytics/strength-test-results/${strId}/slump`,
        dto,
        { observe: 'response', context }
      )
      .pipe(
        map((response) => response),
        catchError((error) => { this.errorService.handleError(error); throw error; })
      );
  }
}
